import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import {
  FileText,
  Buildings,
  Users,
  Clock,
  TrendUp,
  ArrowRight,
  Bell,
  Plus,
  ChartBar,
  ChartPie
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [branchPerformance, setBranchPerformance] = useState([]);
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const requests = [axios.get(`${API_URL}/api/analytics/dashboard`)];
        if (user?.role === 'super_admin') {
          requests.push(axios.get(`${API_URL}/api/analytics/branch-performance`));
        }
        requests.push(axios.get(`${API_URL}/api/analytics/trends?days=30`));

        const responses = await Promise.all(requests);
        setAnalytics(responses[0].data);
        if (user?.role === 'super_admin') {
          setBranchPerformance(responses[1].data);
          setTrends(responses[2].data);
        } else {
          setTrends(responses[1].data);
        }
      } catch (e) {
        console.error('Failed to fetch analytics', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [user?.role]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton"></div>
        <div className="dashboard-grid">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-32 skeleton rounded-md"></div>
          ))}
        </div>
      </div>
    );
  }

  const StatCard = ({ icon: Icon, label, value, color, link }) => (
    <Card className="card-hover" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{label}</p>
            <p className="text-3xl font-bold">{value}</p>
          </div>
          <div className="w-12 h-12 rounded-md flex items-center justify-center" style={{ backgroundColor: color + '20', color: color }}>
            <Icon size={24} weight="duotone" />
          </div>
        </div>
        {link && (
          <Link to={link} className="inline-flex items-center gap-1 text-sm text-primary hover:underline mt-4">
            View all <ArrowRight size={14} />
          </Link>
        )}
      </CardContent>
    </Card>
  );

  // Generate theme-aware chart colors from CSS variables
  const getThemeColor = (cssVar, fallback) => {
    try {
      const val = getComputedStyle(document.documentElement).getPropertyValue(cssVar).trim();
      return val ? `hsl(${val})` : fallback;
    } catch { return fallback; }
  };
  const primary = getThemeColor('--primary', '#1E3A2B');
  const accent = getThemeColor('--accent', '#D95D39');
  const muted = getThemeColor('--muted', '#457B9D');
  const CHART_COLORS = [primary, accent, muted, `${primary}cc`, `${accent}cc`, `${muted}cc`, `${primary}99`, `${accent}99`];

  const statusChartData = (analytics?.status_breakdown || []).filter(s => s.count > 0);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" data-testid="dashboard-welcome">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground mt-1">Here's what's happening with your operations</p>
        </div>
        {user?.role !== 'branch_user' && (
          <Link to="/records/new">
            <Button data-testid="new-record-btn">
              <Plus size={18} className="mr-2" /> New Record
            </Button>
          </Link>
        )}
      </div>

      {/* Stats grid */}
      <div className="dashboard-grid">
        <StatCard icon={FileText} label="Total Records" value={analytics?.total_records || 0} color={primary} link="/records" />
        {user?.role === 'super_admin' && (
          <>
            <StatCard icon={Buildings} label="Active Branches" value={analytics?.total_branches || 0} color={accent} link="/branches" />
            <StatCard icon={Users} label="Total Users" value={analytics?.total_users || 0} color={muted} link="/users" />
          </>
        )}
        <StatCard icon={Bell} label="Notifications" value={analytics?.unread_notifications || 0} color={`${primary}99`} link="/notifications" />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Pie Chart */}
        {statusChartData.length > 0 && (
          <Card data-testid="status-chart">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ChartPie size={20} /> Status Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    dataKey="count"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={3}
                    label={({ name, count }) => `${name} (${count})`}
                    labelLine={false}
                  >
                    {statusChartData.map((entry, idx) => (
                      <Cell key={entry.name} fill={entry.color || CHART_COLORS[idx % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Records']} />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Branch Performance Bar Chart */}
        {user?.role === 'super_admin' && branchPerformance.length > 0 && (
          <Card data-testid="branch-performance-chart">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ChartBar size={20} /> Branch Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={branchPerformance} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="branch_code"
                    tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }}
                  />
                  <YAxis tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                    formatter={(value, name) => [value, name === 'total_records' ? 'Total' : 'Closed']}
                    labelFormatter={(label) => {
                      const b = branchPerformance.find(bp => bp.branch_code === label);
                      return b?.branch_name || label;
                    }}
                  />
                  <Legend formatter={(value) => value === 'total_records' ? 'Total Records' : 'Closed Records'} />
                  <Bar dataKey="total_records" fill="#1E3A2B" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="closed_records" fill="#D95D39" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Trends Area Chart */}
      {trends?.records_trend?.length > 0 && (
        <Card data-testid="trends-chart">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <TrendUp size={20} /> Activity Trends (Last 30 Days)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart
                data={trends.records_trend.map(r => ({
                  date: r._id,
                  records: r.count,
                  interactions: (trends.interactions_trend || []).find(i => i._id === r._id)?.count || 0
                }))}
                margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }}
                  tickFormatter={(v) => v.slice(5)}
                />
                <YAxis tick={{ fontSize: 12, fill: 'var(--muted-foreground)' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: 'var(--card)', border: '1px solid var(--border)', borderRadius: '8px' }}
                />
                <Area type="monotone" dataKey="records" stroke={primary} fill={primary} fillOpacity={0.15} name="New Records" />
                <Area type="monotone" dataKey="interactions" stroke={accent} fill={accent} fillOpacity={0.15} name="Interactions" />
                <Legend />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Recent records & reminders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Recent Records</CardTitle>
            <Link to="/records"><Button variant="ghost" size="sm">View all</Button></Link>
          </CardHeader>
          <CardContent>
            {analytics?.recent_records?.length > 0 ? (
              <div className="space-y-3">
                {analytics.recent_records.slice(0, 5).map(record => (
                  <Link
                    key={record.id}
                    to={`/records/${record.id}`}
                    className="flex items-center justify-between p-3 bg-secondary/50 rounded-md hover:bg-secondary transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {record.data?.name || record.data?.customer_name || `Record ${record.id.slice(0, 8)}`}
                      </p>
                      <p className="text-xs text-muted-foreground">{new Date(record.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="outline">{record.status}</Badge>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="empty-state py-8">
                <FileText size={32} className="mb-2 opacity-50" />
                <p>No records yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Upcoming Reminders</CardTitle>
            <Link to="/reminders"><Button variant="ghost" size="sm">View all</Button></Link>
          </CardHeader>
          <CardContent>
            {analytics?.upcoming_reminders?.length > 0 ? (
              <div className="space-y-3">
                {analytics.upcoming_reminders.map(reminder => (
                  <div key={reminder.id} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-md">
                    <Clock size={18} className="text-primary mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{reminder.title}</p>
                      <p className="text-xs text-muted-foreground">Due: {new Date(reminder.due_date).toLocaleDateString()}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state py-8">
                <Clock size={32} className="mb-2 opacity-50" />
                <p>No upcoming reminders</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;

